import {
  Component,
  OnInit,
  ViewChild,
  ChangeDetectorRef,
  AfterViewInit
} from '@angular/core';
import { ActivityView } from 'src/app/views/master-views/app.view/app.view.component';
import { FormActivityInputViewComponent } from '../../form-activity-editor/form-activity-input/form-activity-input-view/form-activity-input-view.component';
import {
  ActivityStatus,
  ConsolidatedInspectionActivity,
  ConsolidatedInspectionActivityModel,
  ConsolidatedInspectionSections,
  DataEntityLayoutModel,
  FormActivity,
  InspectionStep,
  SingleColumnFormLayoutModel
} from 'src/app/models/activities';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import {
  ActivityUtilities,
  ValidationService,
  WorkflowContextService
} from 'src/app/services';
import { WorkflowApplicationsJanusService } from 'src/app/views/admin/workflow-applications/workflow-applications-janus/workflow-applications-janus.service';
import { LayoutService } from 'src/app/services/layout.service';

@Component({
  selector: 'wm-consolidated-inspection-activity-input',
  templateUrl: './consolidated-inspection-activity-input.component.html',
  styleUrl: './consolidated-inspection-activity-input.component.css',
  standalone: false
})
export class ConsolidatedInspectionActivityInputComponent extends ActivityView
  implements OnInit, AfterViewInit {
  @ViewChild('requestStepInput', { static: false })
  requestStepInput: FormActivityInputViewComponent;
  @ViewChild('scheduleStepInput', { static: false })
  scheduleStepInput: FormActivityInputViewComponent;
  @ViewChild('completeStepInput', { static: false })
  completeStepInput: FormActivityInputViewComponent;

  activity: ConsolidatedInspectionActivity;

  requestStepVisible = false;
  scheduleStepVisible = false;

  requestStepForm: UntypedFormGroup;
  scheduleStepForm: UntypedFormGroup;
  completeStepForm: UntypedFormGroup;

  ActivityStatus = ActivityStatus;
  InspectionStep = InspectionStep;

  expandedStep: InspectionStep;

  uploadedInspectionPending: boolean = false;
  templateForPermitForm: boolean = false;
  processing: boolean = false;

  constructor(
    private cdr: ChangeDetectorRef,
    public context: WorkflowContextService,
    public workflowApplicationsJanusSvc: WorkflowApplicationsJanusService,
    public layoutService: LayoutService
  ) {
    super();
    layoutService.useNewLayout$.subscribe(useNewLayout => {
      this.templateForPermitForm = useNewLayout;
    });
  }

  ngOnInit() {
    this.requestStepForm = new UntypedFormGroup({});
    this.scheduleStepForm = new UntypedFormGroup({});
    this.completeStepForm = new UntypedFormGroup({});

    this.form.addControl(
      'invalidCtrl',
      new UntypedFormControl('', Validators.required)
    );

    if (
      this.activity.model.inspection.uploadedFromTabletOn &&
      this.activity.status !== ActivityStatus.Completed
    ) {
      this.uploadedInspectionPending = true;
    }

    const convertedActivities = ActivityUtilities.convertActivitiesJsonToClasses(
      [
        this.activity.model.requestStepActivity,
        this.activity.model.scheduleStepActivity,
        this.activity.model.completeStepActivity
      ]
    );

    this.activity.model.requestStepActivity = convertedActivities[0] as FormActivity;
    this.activity.model.scheduleStepActivity = convertedActivities[1] as FormActivity;
    this.activity.model.completeStepActivity = convertedActivities[2] as FormActivity;

    // Share the live step entities with the parent used for formula requests.
    // Do this before filtering so mobile-only fields remain in the payload.
    this.reassembleColumnEntities();

    // Hide any Complete Step column entities that were flagged as
    // "No Schedule Only" in the activity editor. They are still present on
    // the parent activity's merged `formLayoutModel.columnEntities` (so the
    // published InspectionApp form still receives them), but they must not
    // render in the workflow-manager Complete Step input — those fields
    // are populated exclusively by the mobile inspection app for
    // No Schedule inspections.
    this.applyNoScheduleOnlyFilter();

    // Populate the step forms with FormControls keyed by each step activity's
    // entity templateCodes. Without this, entity inputs rendered inside the
    // step forms fail to bind (`formControlName` throws "Cannot find control
    // with name: …") and same-activity valueChanges subscriptions used for
    // conditional row/table visibility never wire up.
    this.requestStepForm = new UntypedFormGroup(
      ValidationService.createValidationGroup(
        this.activity.model.requestStepActivity
      ),
      { updateOn: 'blur' }
    );
    this.scheduleStepForm = new UntypedFormGroup(
      ValidationService.createValidationGroup(
        this.activity.model.scheduleStepActivity
      ),
      { updateOn: 'blur' }
    );
    this.completeStepForm = new UntypedFormGroup(
      ValidationService.createValidationGroup(
        this.activity.model.completeStepActivity
      ),
      { updateOn: 'blur' }
    );

    this.requestStepVisible =
      this.activity.requestStepSubmitted ||
      (!this.activity.requestStepSubmitted &&
        !this.activity.scheduleStepSubmitted &&
        this.activity.status === ActivityStatus.InProgress);

    this.scheduleStepVisible =
      this.activity.scheduleStepSubmitted ||
      (!this.activity.scheduleStepSubmitted &&
        this.activity.status === ActivityStatus.InProgress);

    this.expandedStep = this.activity.responsibleForComplete
      ? InspectionStep.Complete
      : this.activity.responsibleForSchedule
      ? InspectionStep.Schedule
      : InspectionStep.Request;
  }

  ngAfterViewInit() {
    this.cdr.detectChanges();
  }

  expandRequestStep() {
    this.expandedStep = InspectionStep.Request;
  }

  expandScheduleStep() {
    if (this.activity.responsibleForSchedule) {
      this.expandedStep = InspectionStep.Schedule;
    }
  }

  expandCompleteStep() {
    if (this.activity.responsibleForComplete) {
      this.expandedStep = InspectionStep.Complete;
    }
  }

  submitRequestStep() {
    this.processing = true;

    this.requestStepInput.persistChildComponent();

    this.prepareStepSubmittal();

    this.activity.submittedStep = InspectionStep.Request;

    const categoriesDefined = this.workflowApplicationsJanusSvc.getData()
      .inlineApplicationSummaryDetails.categoriesDefined;
    if (categoriesDefined) {
      this.workflowApplicationsJanusSvc.setData({
        ...this.workflowApplicationsJanusSvc.getData(),
        consolidatedInspection: { activity: this.activity, processWay: 'Save' }
      });
    } else {
      this.save.emit(true);
      setTimeout(() => {
        this.workflowApplicationsJanusSvc.setData({
          ...this.workflowApplicationsJanusSvc.getData(),
          scrollToExpandedRow: true
        });
      }, 2000);
    }
  }

  submitScheduleStep() {
    this.processing = true;

    this.scheduleStepInput.persistChildComponent();

    this.prepareStepSubmittal();

    this.activity.submittedStep = InspectionStep.Schedule;

    const categoriesDefined = this.workflowApplicationsJanusSvc.getData()
      .inlineApplicationSummaryDetails.categoriesDefined;
    if (categoriesDefined) {
      this.workflowApplicationsJanusSvc.setData({
        ...this.workflowApplicationsJanusSvc.getData(),
        consolidatedInspection: { activity: this.activity, processWay: 'Save' }
      });
    } else {
      this.save.emit(true);
      setTimeout(() => {
        this.workflowApplicationsJanusSvc.setData({
          ...this.workflowApplicationsJanusSvc.getData(),
          scrollToExpandedRow: true
        });
      }, 2000);
    }
  }

  submitCompleteStep() {
    this.processing = true;

    this.completeStepInput.persistChildComponent();

    this.prepareStepSubmittal();

    this.activity.submittedStep = InspectionStep.Complete;
    const categoriesDefined = this.workflowApplicationsJanusSvc.getData()
      .inlineApplicationSummaryDetails.categoriesDefined;
    if (categoriesDefined) {
      this.workflowApplicationsJanusSvc.setData({
        ...this.workflowApplicationsJanusSvc.getData(),
        consolidatedInspection: {
          activity: this.activity,
          processWay: 'Submit'
        }
      });
    } else {
      this.goNext.emit(true);
      setTimeout(() => {
        this.workflowApplicationsJanusSvc.setData({
          ...this.workflowApplicationsJanusSvc.getData(),
          scrollToExpandedRow: true
        });
      }, 2000);
    }
  }

  prepareStepSubmittal() {
    this.reassembleColumnEntities();

    this.resetModelSteps();
  }

  reassembleColumnEntities() {
    // Idempotency guard: once the step activities have been consumed (nulled by a
    // prior resetModelSteps() call), the main columnEntities already hold the merged
    // result. Re-running the merge would clobber them with an empty array.
    // This can happen when more than one CIA input instance shares the same activity
    // reference (e.g., inline Janus edit view + hidden modal-confirm both bind to
    // `editingActivity` in application-readonly).
    if (
      this.activity?.model?.requestStepActivity == null &&
      this.activity?.model?.scheduleStepActivity == null &&
      this.activity?.model?.completeStepActivity == null
    ) {
      return;
    }

    let columnEntities: DataEntityLayoutModel[] = [];

    columnEntities.push(
      ...((this.activity?.model?.requestStepActivity?.model
        ?.formLayoutModel as SingleColumnFormLayoutModel)?.columnEntities ?? [])
    );

    columnEntities.push(
      ...((this.activity?.model?.scheduleStepActivity?.model
        ?.formLayoutModel as SingleColumnFormLayoutModel)?.columnEntities ?? [])
    );

    columnEntities.push(
      ...((this.activity?.model?.completeStepActivity?.model
        ?.formLayoutModel as SingleColumnFormLayoutModel)?.columnEntities ?? [])
    );

    (this.activity?.model
      ?.formLayoutModel as SingleColumnFormLayoutModel).columnEntities = columnEntities;
  }

  resetModelSteps() {
    this.activity.model.requestStepActivity = null;
    this.activity.model.scheduleStepActivity = null;
    this.activity.model.completeStepActivity = null;
  }

  async persistChildComponent() {
    this.prepareStepSubmittal();
  }

  /**
   * Removes Complete Step column entities that were flagged as
   * "No Schedule Only" in the activity editor. The persisted flag lives on
   * `activity.model.noScheduleOnlyColumnEntityIndices` and stores positions
   * into the parent activity's merged `formLayoutModel.columnEntities`
   * (order: request-section entries, then schedule-section entries, then
   * complete-section entries — matching the editor's `save()`). We
   * translate each merged index into a Complete Step-local position by
   * subtracting the count of preceding request/schedule entries.
   */
  private applyNoScheduleOnlyFilter() {
    const indices = (this.activity?.model as ConsolidatedInspectionActivityModel)
      ?.noScheduleOnlyColumnEntityIndices;

    if (!Array.isArray(indices) || indices.length === 0) {
      return;
    }

    const requestLen =
      (this.activity?.model?.requestStepActivity?.model
        ?.formLayoutModel as SingleColumnFormLayoutModel)?.columnEntities
        ?.length ?? 0;

    const scheduleLen =
      (this.activity?.model?.scheduleStepActivity?.model
        ?.formLayoutModel as SingleColumnFormLayoutModel)?.columnEntities
        ?.length ?? 0;

    const completeLayout = this.activity?.model?.completeStepActivity?.model
      ?.formLayoutModel as SingleColumnFormLayoutModel;

    const completeEntries = completeLayout?.columnEntities;

    if (!completeEntries || completeEntries.length === 0) {
      return;
    }

    const offset = requestLen + scheduleLen;

    const localFlagged = new Set<number>();

    for (const mergedIdx of indices) {
      const localIdx = mergedIdx - offset;

      if (localIdx >= 0 && localIdx < completeEntries.length) {
        localFlagged.add(localIdx);
      }
    }

    if (localFlagged.size === 0) {
      return;
    }

    completeLayout.columnEntities = completeEntries.filter(
      (_, i) => !localFlagged.has(i)
    );
  }
}
